import { INFOODS, NutrientTarget, NutrientTargets } from './food';
import { DeclaredState } from './user-state';
import { deepClone, deepCompareEQ } from '../deep-helpers';
import { ActivityLevel } from '../enums';
import { wrapIntegerToInterval } from '../shared-util';
import { hoursToIsoTime, isoTimeToHours, setDecimalHour } from '../time';
import { formatHourMinute24 } from '../date-format-machine';
import { DateTime } from 'luxon';
import { FitnessPlan } from './fitness-plan';
import { createDayStartEntry, Log_DayStart } from './log-day-start';
import { createMealEntry, Log_Meal } from './log-meal';
import { Log_WeighIn } from './log-weigh-in';
import { createWorkoutEntry, Log_Workout } from './log-workout';
import { logEntryHoldsUserData, pickOneEntryPerSingletonSlot } from './log-entry-user-data';
import { PouchDoc } from './pouch';
import { copyEntryLocalTime, createGenericEntry, firstFreeLogEntryId, getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType, Log_Entry, Log_DayEnd, setEntryLocalTime } from './user-log';
import { createWeeklyCheckupEntry } from './log-weekly-checkup';

export enum Schedule_ItemType {
  DayStart = 'daystart',
  Workout = 'workout',
  Meal = 'meal',
  DayEnd = 'dayend',
  WeeklyCheckup = 'weeklycheckup', /** Weekly checkup, used to store weekly summary of the week */
}

export interface Schedule_ItemBase {
  type: Schedule_ItemType;
  time_hr: number;
}

export interface Schedule_ItemDayStart extends Schedule_ItemBase {
  type: Schedule_ItemType.DayStart;
  targets?: NutrientTargets;
}

/**
 * The weekly check-up.
 *
 * `time_hr` on this item is NOT the source of truth for when the check-up
 * happens. It is a snapshot taken when the schedule was first seeded, no screen
 * lets anybody change it, and nothing keeps it following the user's bedtime — so
 * it goes stale and that is fine. What decides when a check-up entry is created,
 * and what time it is filed at, is `handleDayEnd` in `generateScheduleLogEntries`
 * below, which works it out from the day-end ("go to sleep") entry.
 *
 * Do not wire this field into entry generation. The reasons are written out at
 * the `handleDayEnd` fetch site, because that is where somebody is standing when
 * the idea occurs to them.
 */
export interface Schedule_ItemWeeklyCheckup extends Schedule_ItemBase {
  type: Schedule_ItemType.WeeklyCheckup;
}

/**
 * How long before the end of the day the weekly check-up sits.
 *
 * One number, used by both the code that seeds a check-up time into a new
 * schedule and the code that decides when the check-up entry actually happens.
 * They used to be two numbers that disagreed (5 minutes and 10 minutes).
 */
export const WEEKLY_CHECKUP_MINUTES_BEFORE_DAY_END = 10;

/**
 * The weekly check-up's time of day, given the time the user's day ends.
 *
 * Wraps into [0, 24) so a midnight bedtime yields 23:50 rather than a negative
 * number. A negative hour is not a time anybody can display or reason about: it
 * formats as "-1:55", and code that treated it as a real time would land the
 * check-up on the day before, which is exactly how #4424 happened.
 */
export function getWeeklyCheckupTimeHr(dayEndTime_hr: number): number {
  return wrapIntegerToInterval(dayEndTime_hr - WEEKLY_CHECKUP_MINUTES_BEFORE_DAY_END / 60, 24);
}

export function isScheduleItemDayStart(item: Schedule_ItemBase): item is Schedule_ItemDayStart {
  return item.type === Schedule_ItemType.DayStart;
}

export function isScheduleItemDayEnd(item: Schedule_ItemBase): item is Schedule_ItemDayEnd {
  return item.type === Schedule_ItemType.DayEnd;
}

export function isScheduleItemWorkout(item: Schedule_ItemBase): item is Schedule_ItemWorkout {
  return item.type === Schedule_ItemType.Workout;
}

export function isScheduleItemWeeklyCheckup(item: Schedule_ItemBase): item is Schedule_ItemWeeklyCheckup {
  return item.type === Schedule_ItemType.WeeklyCheckup;
}

export interface Schedule_ItemDayEnd extends Schedule_ItemBase {
  type: Schedule_ItemType.DayEnd;
}
export interface Schedule_ItemWorkout extends Schedule_ItemBase {
  type: Schedule_ItemType.Workout;
}

export interface Schedule_ItemMeal extends Schedule_ItemBase {
  type: Schedule_ItemType.Meal;
  targets?: NutrientTargets;
  cutMeal?: boolean;
}
export function isScheduleItemMeal(item: Schedule_ItemBase): item is Schedule_ItemMeal {
  return item.type === Schedule_ItemType.Meal;
}

export type Schedule_Item = Schedule_ItemMeal | Schedule_ItemDayStart | Schedule_ItemDayEnd | Schedule_ItemWorkout | Schedule_ItemWeeklyCheckup;

export interface Schedule_Day {
  items: Schedule_Item[];
}

export enum ScheduleRuleRepeat {
  Never = 'never',
  Daily = 'daily',
  Weekly = 'weekly',
}

export interface ScheduleRule {
  itemType: Schedule_ItemType;
  itemReference?: number; // index of item if there is more than one of the same type
  repeat: ScheduleRuleRepeat;
  weekday?: number; // for weekly repeat goes from 1 to 7
  dateISO: string; // This should be a local date in ISO YYYY-MM-DD format. It defines start date for the rule (and end for never repeating rules)
  endDateISO?: string; // This should be a local date in ISO YYYY-MM-DD format. It defines end date for the rule (if applicable)
  time_hr?: number;
  exclude?: boolean; // if true, this rule is an exclusion rule, meaning it will not be applied to the schedule
  /**
   * Set only when this rule exists because a workout was SKIPPED and moved to the
   * next day: it holds the local date (YYYY-MM-DD) of the workout that was skipped.
   *
   * It is a receipt, so that undoing that skip can remove this rule and nothing
   * else. Without it the undo matched any workout rule dated the next day, and so
   * deleted rules the user had made themselves — an extra workout they added by
   * hand, a rule saying a workout was moved OFF that day, or a whole recurring
   * training day that happened to start then. `WorkoutStateService` writes it, and
   * `ScheduleRuleService.findSkipRescheduledWorkoutRule` is the only reader.
   *
   * Absent on every rule written before this field existed, and on every rule from
   * any other source. Absent means "not mine to delete", which is the safe answer.
   */
  skipRescheduledFromDateISO?: string;
}

// rule is active if query date is on or after start date, and before end date (if any)
export function isRuleActiveOnDate(rule: ScheduleRule, dateISO: string): boolean {
  return dateISO >= rule.dateISO && (!rule.endDateISO || rule.endDateISO > dateISO);
}

/**
 * Find all active weekly rules for a specific weekday and item type.
 * Returns rules sorted by dateISO ascending (oldest first).
 */
export function getActiveWeeklyRules(rules: ScheduleRule[], itemType: Schedule_ItemType, weekday: number, dateISO: string): ScheduleRule[] {
  return rules
    .filter(rule =>
      rule.itemType === itemType &&
      rule.repeat === ScheduleRuleRepeat.Weekly &&
      rule.weekday === weekday &&
      isRuleActiveOnDate(rule, dateISO)
    )
    .sort((a, b) => a.dateISO < b.dateISO ? -1 : a.dateISO > b.dateISO ? 1 : 0);
}

/**
 * Of several rules that all apply, the one that wins: the latest start date, and on a
 * tie the one stored last.
 *
 * Every tier of the cascade below picks its winner this way, so the cascade's answer
 * does not depend on the order of the array it was handed.
 */
function latestStartingRule(candidates: ScheduleRule[]): ScheduleRule | undefined {
  let winner: ScheduleRule | undefined = undefined;
  // for each candidate, keep it if it starts no earlier than the best so far
  for (const candidate of candidates) {
    if (!winner || candidate.dateISO >= winner.dateISO) {
      winner = candidate;
    }
  }
  return winner;
}

/**
 * The weekly rule of this item type that is in force on `dateISO`, whatever weekday it
 * is for. Of several, the one that starts latest wins.
 *
 * Use this to ask "does this user have a weekly workout at all, and at what time" —
 * a question about the pattern itself rather than about one particular day. Use
 * `resolveRecurringRule` when the weekday matters.
 *
 * The answer does not depend on the order the rules are stored in, and a rule that has
 * been ended is never returned.
 */
export function resolveAnyActiveWeeklyRule(
  rules: ScheduleRule[],
  itemType: Schedule_ItemType,
  dateISO: string
): ScheduleRule | undefined {
  return latestStartingRule(rules.filter(rule =>
    rule.itemType === itemType &&
    rule.repeat === ScheduleRuleRepeat.Weekly &&
    isRuleActiveOnDate(rule, dateISO)
  ));
}

/**
 * Everything the rule cascade can say about one item on one date. Both fields come out
 * of a single walk, because the two callers want different halves of it:
 *
 * - The reschedule popover asks only "what time is this item at", and treats a
 *   one-time exclusion as not answering that question — so it reads `timeRule` and
 *   ignores `excludedBy`.
 * - The schedule build asks "is this item on this day, and when" — so an exclusion
 *   means the item is left off the day entirely, whatever `timeRule` says.
 *
 * Neither reading is wrong; they are different questions. Returning both facts is what
 * lets one walk serve both without a mode argument.
 */
export interface ScheduleRuleResolution {
  /** the one-time rule that removes this item from this date, if there is one */
  excludedBy?: ScheduleRule;
  /** the rule that gives this item its time, ignoring any exclusion */
  timeRule?: ScheduleRule;
}

/**
 * Resolve the RECURRING tiers of the rule cascade for an item on a given date: a weekly
 * rule for this weekday, else a daily rule. Both are checked against the rule's own
 * start and end dates, so a rule that has been ended is never returned.
 *
 * Use this to ask about the pattern a user repeats week after week. Use
 * `resolveScheduleRule` to ask what happens on one particular date, which also
 * considers a one-time rule written for that date alone.
 */
export function resolveRecurringRule(
  rules: ScheduleRule[],
  itemType: Schedule_ItemType,
  dateISO: string,
  weekday: number,
  itemReference?: number
): ScheduleRule | undefined {
  // weekly rule for this weekday
  const weekly = latestStartingRule(
    getActiveWeeklyRules(rules, itemType, weekday, dateISO)
      .filter(rule => rule.itemReference === itemReference)
  );
  if (weekly) return weekly;

  // else daily rule
  return latestStartingRule(rules.filter(rule =>
    rule.itemType === itemType &&
    rule.repeat === ScheduleRuleRepeat.Daily &&
    rule.itemReference === itemReference &&
    isRuleActiveOnDate(rule, dateISO)
  ));
}

/**
 * Resolve the full rule cascade for an item on a given date: a one-time rule written for
 * exactly this date wins, else the recurring tiers (weekly for this weekday, else
 * daily). Every tier is checked against the rule's own start and end dates.
 *
 * This is the one place the cascade is written. Callers differ only in which half of
 * the answer they use — see `ScheduleRuleResolution`.
 */
export function resolveScheduleRule(
  rules: ScheduleRule[],
  itemType: Schedule_ItemType,
  dateISO: string,
  weekday: number,
  itemReference?: number
): ScheduleRuleResolution {
  const oneTimeRules = rules.filter(rule =>
    rule.itemType === itemType &&
    rule.repeat === ScheduleRuleRepeat.Never &&
    rule.dateISO === dateISO &&
    rule.itemReference === itemReference &&
    // an "every day" reschedule ends a one-off it overrides by giving it an end date
    // (#6040). An ended one-off must stop winning on and after that date, or the
    // earlier "this day only" time beats the newer every-day one.
    isRuleActiveOnDate(rule, dateISO)
  );

  // if any one-time rule for this date removes the item, that is the answer to the
  // "is it on this day" question. An exclusion and a placement for the same date can
  // only come from a damaged or sync-conflicted document — every writer replaces one
  // with the other — and letting the exclusion win keeps the answer independent of
  // which of the two happens to be stored first.
  const excludedBy = oneTimeRules.find(rule => rule.exclude);

  const oneTimeTime = latestStartingRule(oneTimeRules.filter(rule => !rule.exclude));
  const timeRule = oneTimeTime ?? resolveRecurringRule(rules, itemType, dateISO, weekday, itemReference);

  return { excludedBy, timeRule };
}

/**
 * The time an item happens on a given date, or undefined when no rule in force gives it
 * one. A one-time rule that REMOVES the item from this date does not give it a time, so
 * the cascade carries on past it to the weekly and daily tiers — which is what the
 * reschedule popover wants when it asks what time a sibling item sits at.
 */
export function getEffectiveRuleTime(
  rules: ScheduleRule[],
  itemType: Schedule_ItemType,
  dateISO: string,
  weekday: number,
  itemReference?: number
): number | undefined {
  return resolveScheduleRule(rules, itemType, dateISO, weekday, itemReference).timeRule?.time_hr;
}

/**
 * Expire all active weekly rules for a specific weekday and item type
 * by setting endDateISO. Returns the number of rules expired.
 */
export function expireActiveRulesForWeekday(rules: ScheduleRule[], itemType: Schedule_ItemType, weekday: number, dateISO: string): number {
  const activeRules = getActiveWeeklyRules(rules, itemType, weekday, dateISO);
  for (const rule of activeRules) {
    rule.endDateISO = dateISO;
  }
  return activeRules.length;
}

/**
 * Deduplicate active weekly workout rules: for each weekday with multiple
 * active rules, keep the newest (highest dateISO) and expire the rest.
 * Returns true if any deduplication was performed.
 */
export function deduplicateActiveWorkoutRules(rules: ScheduleRule[], dateISO: string): boolean {
  let modified = false;
  const rulesByWeekday = new Map<number, ScheduleRule[]>();

  for (const rule of rules) {
    if (rule.itemType === Schedule_ItemType.Workout &&
        rule.repeat === ScheduleRuleRepeat.Weekly &&
        isRuleActiveOnDate(rule, dateISO)) {
      const existing = rulesByWeekday.get(rule.weekday) || [];
      existing.push(rule);
      rulesByWeekday.set(rule.weekday, existing);
    }
  }

  for (const [, weekdayRules] of rulesByWeekday) {
    if (weekdayRules.length <= 1) continue;

    // keep the newest rule (highest dateISO)
    weekdayRules.sort((a, b) => a.dateISO < b.dateISO ? -1 : a.dateISO > b.dateISO ? 1 : 0);
    const newest = weekdayRules[weekdayRules.length - 1];
    for (const rule of weekdayRules) {
      if (rule !== newest) {
        rule.endDateISO = dateISO;
        modified = true;
      }
    }
  }

  return modified;
}

export interface Schedule extends PouchDoc {
  /** Set if the user has modified the schedule and it hasn't yet been applied to the planned workouts/meals/... */
  days?: Schedule_Day[]; // <<< this is legacy data, converted on load and no longer used!
  rules?: ScheduleRule[];
}

export function emptySchedule() {
  return {
    days: [],
    rules: [],
  } as Schedule;
}

/// Whether Intermittent Fasting should be used, based on the state.
export function usesIF(declaredState: DeclaredState) {
  // note comparing with false, because if it is undefined (old accounts) we _don't_ use IF until they update their plan
  return (
    declaredState.morningAppetite === false &&
    declaredState.activityLevel <= ActivityLevel.SomewhatActive &&
    declaredState.mealFrequency <= 3
  );
}

function getDefaultWakeupAndBedTimeHours(declaredState: DeclaredState): [number, number] {
  let wakeupHours = isoTimeToHours(declaredState.wakeupTime);
  let bedHours = isoTimeToHours(declaredState.bedTime);
  // sanity - any weird numbers that don't make sense, snap them to something sensible
  if (wakeupHours < 1) {
    wakeupHours = 1;
  }
  if (bedHours > 23.75) {
    bedHours = 23.75;
  }
  // we don't support sleeping after midnight (for now! see issue #65)
  if (bedHours < wakeupHours) {
    wakeupHours = 8;
    bedHours = 22;
  }

  // sleeping for way too much or way too little
  let sleepingTime = 24 - (bedHours - wakeupHours);
  if (sleepingTime > 14 || sleepingTime < 5) {
    // probably trolling, force somewhat "traditional" times
    wakeupHours = 7;
    bedHours = 22;
  }

  return [wakeupHours, bedHours];
}

function getDefaultMealTimes(declaredState: DeclaredState, wakeupHours: number, bedHours: number, workoutHours?: number): number[] {
  let useIF = usesIF(declaredState);
  let numMeals = declaredState.mealFrequency;

  // local function using wakeupHours, bedHours, useIF from closure
  function calculateMealHours(desiredFirst: number, desiredLast: number, numMeals: number): number[] {
    // clamp to valid waking hours
    const mealFirst = Math.max(wakeupHours + 1, desiredFirst);
    const mealLast = Math.min(bedHours - 0.5, desiredLast);

    let mealHours: number[];
    if (numMeals === 1) {
      // for OMAD, use end of window (IF) or start (non-IF)
      mealHours = useIF ? [mealLast] : [mealFirst];
    } else if (numMeals === 2) {
      mealHours = [mealFirst, mealLast];
    } else {
      // distribute meals evenly across the eating window
      mealHours = [];
      for (let i = 0; i < numMeals; i++) {
        mealHours.push(mealFirst + (mealLast - mealFirst) * i / (numMeals - 1));
      }
    }

    // round all times to 15 minutes
    return mealHours.map(h => Math.round(h * 4) / 4);
  }

  // determine eating window duration
  let eatingWindow: number;
  if (numMeals === 3) {
    eatingWindow = 10;
  } else if (numMeals <= 2) {
    eatingWindow = 8;
  } else {
    eatingWindow = 12;
  }

  let mealHours: number[];
  if (useIF) {
    // favor eating in the evening and fasting in the morning
    const desiredLast = bedHours - 1;
    const desiredFirst = desiredLast - eatingWindow;
    mealHours = calculateMealHours(desiredFirst, desiredLast, numMeals);
  } else {
    // spread meals across the day
    const desiredFirst = wakeupHours + 1;
    const desiredLast = bedHours - 1;
    mealHours = calculateMealHours(desiredFirst, desiredLast, numMeals);
  }

  if (workoutHours) {
    for (let iMeal = 0; iMeal < numMeals; ++iMeal) {
      if (workoutHours > mealHours[iMeal] - 0.5 && workoutHours < mealHours[iMeal]) {
        mealHours[iMeal] = workoutHours + 0.5;
      } else if (workoutHours < mealHours[iMeal] + 0.5 && workoutHours > mealHours[iMeal]) {
        mealHours[iMeal] = workoutHours - 0.5;
      }
    }
  }

  return mealHours;
}

export function countMealPerDayFromScheduleRules(rules: ScheduleRule[]): number {
  const mealRules = rules.filter(rule => rule.itemType === Schedule_ItemType.Meal);
  const mealReferences = mealRules.map(rule => rule.itemReference).filter((value, index, self) => self.indexOf(value) === index);
  return mealReferences.length;
}

// if the active weekly workout rules match the declared training days
export function doActiveWorkoutDaysMatchDeclared(rules: ScheduleRule[], trainingDays: boolean[], dateISO: string): boolean {
  for (let weekday = 1; weekday <= 7; ++weekday) {
    const isDeclaredWorkoutDay = trainingDays[weekday - 1];
    const hasActiveRule = rules.some(rule =>
      rule.itemType === Schedule_ItemType.Workout &&
      rule.repeat === ScheduleRuleRepeat.Weekly &&
      rule.weekday === weekday &&
      isRuleActiveOnDate(rule, dateISO));
    if (isDeclaredWorkoutDay !== hasActiveRule) {
      return false;
    }
  }
  return true;
}

export function generateScheduleDaysFromRules(rules: ScheduleRule[], startDate_local: DateTime, endDate_local: DateTime): Schedule_Day[] {
  const scheduleDays: Schedule_Day[] = [];
  if (!rules || rules.length === 0) {
    return scheduleDays;
  }

  // Read the rules in whatever order they are stored in, and never re-order them. The
  // caller normally hands us the user's live schedule document, which every other reader
  // in the app holds too, so re-ordering it here would change what those readers see and
  // the next save would write the new order to the database. Nothing below needs an
  // order: `resolveScheduleRule` picks the latest-starting rule in each tier itself.
  const dayStartRules = rules.filter(rule => rule.itemType === Schedule_ItemType.DayStart);
  const dayEndRules = rules.filter(rule => rule.itemType === Schedule_ItemType.DayEnd);
  const mealRules = rules.filter(rule => rule.itemType === Schedule_ItemType.Meal);
  // Count different meal references/numbers from rules
  const mealsReferences = mealRules.map(rule => rule.itemReference).filter((value, index, self) => self.indexOf(value) === index);
  const workoutRules = rules.filter(rule => rule.itemType === Schedule_ItemType.Workout);
  const weeklyCheckupRules = rules.filter(rule => rule.itemType === Schedule_ItemType.WeeklyCheckup);

  for (
    let date_local = startDate_local;
    date_local < endDate_local;
    date_local = date_local.plus({ days: 1 })) {

    const day = date_local.toISODate();
    const weekday = date_local.weekday;
    // write this day's time onto the item, and report whether the item belongs on the
    // day at all. `rules` is already narrowed to one item type by the caller.
    const applyRuleToItem = (rules: ScheduleRule[], item: Schedule_Item, itemReference?: number): boolean => {
      const resolution = resolveScheduleRule(rules, item.type, day, weekday, itemReference);
      // if a one-time rule removes the item from this date, it is not on the day
      if (resolution.excludedBy) {
        return false;
      }
      if (!resolution.timeRule) {
        return false;
      }
      item.time_hr = resolution.timeRule.time_hr;
      return true;
    }

    let scheduleDayItems: Schedule_Item[] = [];

    // Add weekly checkup item
    {
      const weeklyCheckupItem: Schedule_ItemWeeklyCheckup = {
        type: Schedule_ItemType.WeeklyCheckup,
        time_hr: undefined,
      }
      if (applyRuleToItem(weeklyCheckupRules, weeklyCheckupItem)) {
        scheduleDayItems.push(weeklyCheckupItem);
      }
    }

    // Add day start item
    {
      const dayStartItem: Schedule_ItemDayStart = {
        type: Schedule_ItemType.DayStart,
        time_hr: undefined,
      }
      if (applyRuleToItem(dayStartRules, dayStartItem)) {
        scheduleDayItems.push(dayStartItem);
      }
    }

    // Add workout item
    {
      const workoutItem: Schedule_ItemWorkout = {
        type: Schedule_ItemType.Workout,
        time_hr: undefined,
      }
      if (applyRuleToItem(workoutRules, workoutItem)) {
        scheduleDayItems.push(workoutItem);
      }
    }

    // Add meal items
    {
      for (let iMealRef = 0; iMealRef < mealsReferences.length; ++iMealRef) {
        const mealRef = mealsReferences[iMealRef];
        const mealItem: Schedule_ItemMeal = {
          type: Schedule_ItemType.Meal,
          time_hr: undefined,
        }
        if (applyRuleToItem(mealRules, mealItem, mealRef)) {
          scheduleDayItems.push(mealItem);
        }
      }
    }

    // Add day end item
    {
      const dayEndItem: Schedule_ItemDayEnd = {
        type: Schedule_ItemType.DayEnd,
        time_hr: undefined,
      }
      if (applyRuleToItem(dayEndRules, dayEndItem)) {
        scheduleDayItems.push(dayEndItem);
      }
    }

    scheduleDayItems = scheduleDayItems.sort((a, b) => a.time_hr - b.time_hr);

    scheduleDays.push({ items: scheduleDayItems });
  }

  return scheduleDays;
}

// find the first upcoming workout date from schedule rules within the next lookAheadDays
export function getNextWorkoutDateISO(rules: ScheduleRule[], fromDate_local: DateTime, lookAheadDays = 7): string | null {
  const scheduleDays = generateScheduleDaysFromRules(rules, fromDate_local, fromDate_local.plus({ days: lookAheadDays }));
  for (let i = 0; i < scheduleDays.length; i++) {
    if (scheduleDays[i].items.some(item => item.type === Schedule_ItemType.Workout)) {
      return fromDate_local.plus({ days: i }).toISODate();
    }
  }
  return null;
}

export function convertScheduleDaysToRules(scheduleDays: Schedule_Day[], ruleStartDateISO: string): ScheduleRule[] {
  const rules: ScheduleRule[] = [];
  let dayStartTimes: number[] = [];
  let dayEndTimes: number[] = [];
  let mealsTimes: number[][] = [];
  for (let day of scheduleDays) {
    const dayStartItem = day.items.find(item => isScheduleItemDayStart(item));
    if (dayStartItem) {
      dayStartTimes.push(dayStartItem.time_hr);
    }

    const dayEndItem = day.items.find(item => isScheduleItemDayEnd(item));
    if (dayEndItem) {
      dayEndTimes.push(dayEndItem.time_hr);
    }

    const meals = day.items.filter(item => isScheduleItemMeal(item));
    for (let iMeal = 0; iMeal < meals.length; iMeal++) {
      const mealItem = meals[iMeal];
      if (!mealsTimes[iMeal]) {
        mealsTimes[iMeal] = [];
      }
      mealsTimes[iMeal].push(mealItem.time_hr);
    }
  }

  let findMostFrequentTime = (times: number[]): number => {
    const frequencyMap = new Map<number, number>();
    for (const time of times) {
      frequencyMap.set(time, (frequencyMap.get(time) || 0) + 1);
    }

    let mostFrequentTime = null;
    let maxFrequency = 0;
    for (const [time, frequency] of frequencyMap.entries()) {
      if (frequency > maxFrequency) {
        maxFrequency = frequency;
        mostFrequentTime = time;
      }
    }
    return mostFrequentTime;
  }

  // Find most frequent day start
  {
    let mostFrequentTime = findMostFrequentTime(dayStartTimes);
    let dayStartScheduleGeneralRule: ScheduleRule = {
      itemType: Schedule_ItemType.DayStart,
      repeat: ScheduleRuleRepeat.Daily,
      time_hr: mostFrequentTime,
      dateISO: ruleStartDateISO,
    }
    rules.push(dayStartScheduleGeneralRule);
    for (let iDay = 0; iDay < dayStartTimes.length; iDay++) {
      if (dayStartTimes[iDay] !== mostFrequentTime) {
        let rule: ScheduleRule = {
          itemType: Schedule_ItemType.DayStart,
          repeat: ScheduleRuleRepeat.Weekly,
          weekday: iDay + 1,
          time_hr: dayStartTimes[iDay],
          dateISO: ruleStartDateISO,
        }
        rules.push(rule);
      }
    }
  }

  // Generate rules for day end
  {
    let mostFrequentTime = findMostFrequentTime(dayEndTimes);
    let dayScheduleGeneralRule: ScheduleRule = {
      itemType: Schedule_ItemType.DayEnd,
      repeat: ScheduleRuleRepeat.Daily,
      time_hr: mostFrequentTime,
      dateISO: ruleStartDateISO,
    }
    rules.push(dayScheduleGeneralRule);
    for (let iDay = 0; iDay < dayEndTimes.length; iDay++) {
      if (dayEndTimes[iDay] !== mostFrequentTime) {
        let rule: ScheduleRule = {
          itemType: Schedule_ItemType.DayEnd,
          repeat: ScheduleRuleRepeat.Weekly,
          weekday: iDay + 1,
          time_hr: dayEndTimes[iDay],
          dateISO: ruleStartDateISO,
        }
        rules.push(rule);
      }
    }
  }

  {
    // Generate weekly checkup
    for (let iDay = 0; iDay < scheduleDays.length; iDay++) {
      let day = scheduleDays[iDay];
      const workoutItem = day.items.find(item => isScheduleItemWeeklyCheckup(item));
      if (workoutItem) {
        let rule: ScheduleRule = {
          itemType: Schedule_ItemType.WeeklyCheckup,
          repeat: ScheduleRuleRepeat.Weekly,
          weekday: iDay + 1,
          time_hr: workoutItem.time_hr,
          dateISO: ruleStartDateISO,
        }
        rules.push(rule);
        break;
      }
    }
  }

  // Generate workout rules
  for (let iDay = 0; iDay < scheduleDays.length; iDay++) {
    let day = scheduleDays[iDay];
    const workoutItem = day.items.find(item => isScheduleItemWorkout(item));
    if (workoutItem) {
      let rule: ScheduleRule = {
        itemType: Schedule_ItemType.Workout,
        repeat: ScheduleRuleRepeat.Weekly,
        weekday: iDay + 1,
        time_hr: workoutItem.time_hr,
        dateISO: ruleStartDateISO,
      }
      rules.push(rule);
    }
  }

  // Generate meal rules
  {
    for (let iMeal = 0; iMeal < mealsTimes.length; iMeal++) {
      let mostFrequentTime = findMostFrequentTime(mealsTimes[iMeal]);
      let mealScheduleGeneralRule: ScheduleRule = {
        itemType: Schedule_ItemType.Meal,
        repeat: ScheduleRuleRepeat.Daily,
        itemReference: iMeal,
        time_hr: mostFrequentTime,
        dateISO: ruleStartDateISO,
      }
      rules.push(mealScheduleGeneralRule);
      for (let iDay = 0; iDay < scheduleDays.length; iDay++) {
        let mealTimes = mealsTimes[iMeal];
        if (mealTimes[iDay] !== mostFrequentTime) {
          let rule: ScheduleRule = {
            itemType: Schedule_ItemType.Meal,
            repeat: ScheduleRuleRepeat.Weekly,
            itemReference: iMeal,
            weekday: iDay + 1,
            time_hr: mealTimes[iDay],
            dateISO: ruleStartDateISO,
          }
          rules.push(rule);
        }
      }

    }
  }

  return rules;
}

function generateScheduleDayItems(declaredState: DeclaredState, wakeupHours: number, bedHours: number, mealHours: number[], workoutHours?: number): Schedule_Item[] {
  let items: Schedule_Item[] = [];

  items.push({
    type: Schedule_ItemType.DayStart,
    time_hr: wakeupHours,
    targets: {}
  });

  let createMeal = (hours: number): Schedule_ItemMeal => ({
    type: Schedule_ItemType.Meal,
    time_hr: hours,
    targets: {},
  });

  for (let iMeal = 0; iMeal < declaredState.mealFrequency; iMeal++) {
    items.push(createMeal(mealHours[iMeal]));
  }

  if (workoutHours) {
    items.push({
      type: Schedule_ItemType.Workout,
      time_hr: workoutHours,
    });
  }

  items.push({
    type: Schedule_ItemType.DayEnd,
    time_hr: bedHours,
  });

  return items.sort((a, b) => { return a.time_hr - b.time_hr; });
}

// This function modifies the schedule based on the declared state and the rule start date.
export function adjustScheduleToDeclaredState(schedule: Schedule, declaredState: DeclaredState, ruleStartDateISO: string) {
  let lastWorkoutHours: number = undefined;
  const allActiveWorkoutRules = schedule.rules.filter(rule => rule.itemType === Schedule_ItemType.Workout && rule.repeat === ScheduleRuleRepeat.Weekly && isRuleActiveOnDate(rule, ruleStartDateISO));
  if (allActiveWorkoutRules.length > 0) {
    // find workout with highest weekday
    const maxWeekday = Math.max(...allActiveWorkoutRules.map(rule => rule.weekday));
    lastWorkoutHours = allActiveWorkoutRules.find(rule => rule.weekday === maxWeekday).time_hr;
  }

  // the wake-up and bed times the user declared in their profile. They stand in for any
  // weekday whose own wake-up or bed-time rule has been ended and never replaced, which
  // a damaged or part-synced schedule document can leave behind. Without them
  // getDefaultMealTimes would be handed an undefined hour and lay the day's meals out at
  // invalid times.
  //
  // A declared state written by an older version of the app may carry neither time, and
  // then there is nothing to stand in with: leave both undefined and behave as this
  // function did before the fallback existed, rather than reading a missing field.
  const hasDeclaredTimes = typeof declaredState.wakeupTime === 'string' && typeof declaredState.bedTime === 'string';
  const [declaredWakeUpHours, declaredEndHours] = hasDeclaredTimes
    ? getDefaultWakeupAndBedTimeHours(declaredState)
    : [undefined, undefined];

  // a meal-count change lays every weekday's meals out afresh. Decide it before the loop
  // below changes any rule: the first weekday to see the change expires the extra meals
  // for the whole week, so later weekdays would no longer see it.
  let isMealCountChangedOnAnyWeekday = false;
  for (let weekday = 1; weekday <= 7; ++weekday) {
    if (getDayMealRules(schedule.rules, weekday, ruleStartDateISO).length !== declaredState.mealFrequency) {
      isMealCountChangedOnAnyWeekday = true;
    }
  }
  // each weekday's own fresh meal layout, indexed by weekday then meal reference. Written
  // onto the rules only after the loop, because the shared daily meal rules are read by
  // every weekday (df-c3a5eea4a9a8).
  const mealLayoutByWeekday: number[][] = [];

  for (let weekday = 1; weekday <= 7; ++weekday) {
    const isDeclaredWorkoutDay = declaredState.trainingDays[weekday - 1];
    // get ALL active weekly workout rules for this weekday (sorted oldest-first)
    const dayWorkouts = getActiveWeeklyRules(schedule.rules, Schedule_ItemType.Workout, weekday, ruleStartDateISO);
    // use the newest rule as the primary one
    const dayWorkout = dayWorkouts.length > 0 ? dayWorkouts[dayWorkouts.length - 1] : undefined;
    // the recurring wake-up and bed times for this weekday. Recurring tiers only, no
    // one-time rules: this function is rewriting the pattern the user repeats every
    // week, and a rule written for one date says nothing about that pattern.
    const dayWakeUp = resolveRecurringRule(schedule.rules, Schedule_ItemType.DayStart, ruleStartDateISO, weekday);
    const dayEnd = resolveRecurringRule(schedule.rules, Schedule_ItemType.DayEnd, ruleStartDateISO, weekday);
    let dayMeals = getDayMealRules(schedule.rules, weekday, ruleStartDateISO);
    // fall back to the declared profile times when no rule is in force for this weekday,
    // and when a rule is in force but carries no usable hour
    const dayWakeUpHours = typeof dayWakeUp?.time_hr === 'number' ? dayWakeUp.time_hr : declaredWakeUpHours;
    const dayEndHours = typeof dayEnd?.time_hr === 'number' ? dayEnd.time_hr : declaredEndHours;
    const currentDayMealsCount = dayMeals.length;
    const mealFrequencyChanged = currentDayMealsCount !== declaredState.mealFrequency;
    const workoutDayChanged = isDeclaredWorkoutDay !== (dayWorkout !== undefined);
    // use workout time from previous schedule if possible
    let workoutHours = isDeclaredWorkoutDay && dayWorkout && dayWorkout.time_hr;
    // if the meal count changed, lay this weekday's meals out from its own day
    const dayMealLayout = (isMealCountChangedOnAnyWeekday || mealFrequencyChanged)
      ? getDefaultMealTimes(declaredState, dayWakeUpHours, dayEndHours, workoutHours)
      : undefined;
    if (dayMealLayout) {
      mealLayoutByWeekday[weekday] = dayMealLayout;
    }
    if (mealFrequencyChanged || workoutDayChanged) {
      let mealHours = dayMeals.map(meal => meal.time_hr);
      if (mealFrequencyChanged) {
        if (declaredState.mealFrequency > currentDayMealsCount) {
          // find which itemReference is missing from dayMeals
          const existingReferences = dayMeals.map(meal => meal.itemReference);
          const missingReferences: number[] = [];
          for (let ref = 0; ref < declaredState.mealFrequency; ++ref) {
            if (!existingReferences.includes(ref)) {
              missingReferences.push(ref);
            }
          }

          // if there are missing references, we need to add them
          if (missingReferences.length > 0) {
            for (let ref of missingReferences) {
              let newRule: ScheduleRule = {
                itemType: Schedule_ItemType.Meal,
                repeat: ScheduleRuleRepeat.Weekly,
                weekday: weekday,
                itemReference: ref,
                time_hr: dayMealLayout[ref],
                dateISO: ruleStartDateISO,
              }

              schedule.rules.push(newRule);

              dayMeals.splice(ref, 0, newRule);
            }
          }
        } else {
          // expire excess meal rules instead of deleting them (preserves history)
          for (const rule of schedule.rules) {
            if (rule.itemType === Schedule_ItemType.Meal &&
              rule.itemReference >= declaredState.mealFrequency &&
              (rule.repeat === ScheduleRuleRepeat.Weekly || rule.repeat === ScheduleRuleRepeat.Daily) &&
              isRuleActiveOnDate(rule, ruleStartDateISO)) {
              rule.endDateISO = ruleStartDateISO;
            }
          }

          dayMeals = dayMeals.filter(meal => meal.itemReference < declaredState.mealFrequency);
        }
      }
      // if the meals are being laid out afresh, place the workout against this weekday's
      // new meal times, not the old ones still stored on the rules. The layout has no time
      // for a meal above the declared count, which a damaged schedule with a gap in its
      // meal references can still carry here, so leave such a meal out.
      if (dayMealLayout) {
        mealHours = dayMeals
          .filter(meal => meal.itemReference < declaredState.mealFrequency)
          .map(meal => dayMealLayout[meal.itemReference]);
      }
      // check if day is declared as workout day
      if (workoutDayChanged || (isDeclaredWorkoutDay && mealFrequencyChanged)) {
        if (isDeclaredWorkoutDay) {
          // if there is no workout
          if (!workoutHours) {
            // if this is a new training day use workout time from last workout or put workout between 2nd and 3rd meal
            workoutHours = lastWorkoutHours;
          }

          // fix workout time if too close to some other item
          if (Math.abs(workoutHours - dayWakeUpHours) < 0.5) {
            workoutHours = dayWakeUpHours + 0.5;
          }
          if (dayEndHours <= workoutHours) {
            workoutHours = dayEndHours - 1.5;
          }
          const mealIndexAfterWorkout = mealHours.findIndex(t => t >= workoutHours);
          if (mealIndexAfterWorkout > -1) {
            if (Math.abs(mealHours[mealIndexAfterWorkout] - workoutHours) <= 1.5) {
              workoutHours = mealHours[mealIndexAfterWorkout] + 1;
            }
          } else {
            workoutHours = mealHours[mealHours.length - 1] - 1.5;
          }

          lastWorkoutHours = workoutHours;
          if (dayWorkout) {
            dayWorkout.time_hr = workoutHours;
            // expire any duplicate active rules for this weekday (keep only the primary)
            for (const rule of dayWorkouts) {
              if (rule !== dayWorkout) {
                rule.endDateISO = ruleStartDateISO;
              }
            }
          } else {
            schedule.rules.push({
              itemType: Schedule_ItemType.Workout,
              repeat: ScheduleRuleRepeat.Weekly,
              weekday: weekday,
              time_hr: workoutHours,
              dateISO: ruleStartDateISO,
            })
          }
        } else {
          // expire ALL active workout rules for this weekday (not just one)
          expireActiveRulesForWeekday(schedule.rules, Schedule_ItemType.Workout, weekday, ruleStartDateISO);
        }
      }
    } else if (dayWorkouts.length > 1) {
      // no meal/workout change needed, but deduplicate if multiple active rules exist
      for (const rule of dayWorkouts) {
        if (rule !== dayWorkout) {
          rule.endDateISO = ruleStartDateISO;
        }
      }
    }
  }

  applyMealLayoutPerWeekday(schedule.rules, mealLayoutByWeekday, declaredState.mealFrequency, ruleStartDateISO);
  consolidateScheduleRules(schedule.rules, ruleStartDateISO);
}

/**
 * The meal rules one weekday reads, in meal-reference order: the shared daily rules, with
 * this weekday's own weekly rule standing in for a daily one wherever it has one.
 *
 * Every weekly rule in force for this weekday is included, whatever its reference. A meal
 * slot added while weekdays had different wake-up or bed times exists only as weekly
 * rules, and leaving it out hid it when the meals per day went down again, so it was
 * never ended (df-2b41ed4921ab).
 */
function getDayMealRules(rules: ScheduleRule[], weekday: number, dateISO: string): ScheduleRule[] {
  const activeMealRules = rules.filter(rule => rule.itemType === Schedule_ItemType.Meal && isRuleActiveOnDate(rule, dateISO));
  const dayMeals = activeMealRules.filter(rule => rule.repeat === ScheduleRuleRepeat.Daily);
  // for each reference, this weekday's first weekly rule replaces the daily one, or is added
  const weeklyReferencesSeen = new Set<number>();
  for (const rule of activeMealRules) {
    if (rule.weekday !== weekday || weeklyReferencesSeen.has(rule.itemReference)) continue;
    weeklyReferencesSeen.add(rule.itemReference);
    const dailyIndex = dayMeals.findIndex(meal => meal.itemReference === rule.itemReference);
    if (dailyIndex > -1) {
      dayMeals.splice(dailyIndex, 1, rule);
    } else {
      dayMeals.push(rule);
    }
  }
  return dayMeals.sort((a, b) => a.itemReference - b.itemReference);
}

/**
 * Write each weekday's fresh meal layout onto the meal rules (df-c3a5eea4a9a8). A weekday's
 * own weekly meal rule takes that weekday's time. A shared daily meal rule is read by
 * several weekdays at once, so it takes the time most of them agree on, and each of them
 * whose own time differs gets a weekly rule of its own. When every weekday agrees — the
 * usual case — only the daily rules change.
 *
 * `mealLayoutByWeekday` has no entry for a weekday that was not laid out; that weekday's
 * rules are left as they are and it does not count towards the daily rule's time.
 */
function applyMealLayoutPerWeekday(rules: ScheduleRule[], mealLayoutByWeekday: number[][], mealFrequency: number, dateISO: string) {
  for (let ref = 0; ref < mealFrequency; ++ref) {
    // this meal's time on each weekday, indexed by weekday. A weekday with no usable time
    // is left out — a damaged document with no wake-up or bed time anywhere lays meals
    // out at NaN, and writing that would only spread the damage into new rules.
    const timeByWeekday: (number | undefined)[] = [];
    for (let weekday = 1; weekday <= 7; ++weekday) {
      const time = mealLayoutByWeekday[weekday]?.[ref];
      if (Number.isFinite(time)) {
        timeByWeekday[weekday] = time;
      }
    }
    applyMealTimesForOneMeal(rules, ref, timeByWeekday, dateISO);
  }
}

/**
 * Write one meal's per-weekday times onto its rules — the three steps described on
 * `applyMealLayoutPerWeekday`. A weekday with no entry in `timeByWeekday` is left alone.
 */
function applyMealTimesForOneMeal(rules: ScheduleRule[], ref: number, timeByWeekday: (number | undefined)[], dateISO: string) {
  const laidOutWeekdays = [1, 2, 3, 4, 5, 6, 7].filter(weekday => timeByWeekday[weekday] !== undefined);

  // for each laid-out weekday, its own weekly rules take its own time; the rest read the daily rule
  const weekdaysOnDailyRule: number[] = [];
  for (const weekday of laidOutWeekdays) {
    const weeklyRules = getActiveWeeklyRules(rules, Schedule_ItemType.Meal, weekday, dateISO).filter(rule => rule.itemReference === ref);
    weeklyRules.forEach(rule => rule.time_hr = timeByWeekday[weekday]);
    if (weeklyRules.length === 0) {
      weekdaysOnDailyRule.push(weekday);
    }
  }

  // for each one-time ("this day only") meal rule already in force, take its own date's
  // weekday time. A meal-count change resets every meal time in force, one-offs included,
  // as it did before the per-weekday layout.
  for (const rule of rules) {
    if (rule.itemType === Schedule_ItemType.Meal && rule.repeat === ScheduleRuleRepeat.Never &&
      rule.itemReference === ref && isRuleActiveOnDate(rule, dateISO)) {
      const time = timeByWeekday[DateTime.fromISO(rule.dateISO).weekday];
      if (time !== undefined) {
        rule.time_hr = time;
      }
    }
  }

  const dailyRules = rules.filter(rule =>
    rule.itemType === Schedule_ItemType.Meal &&
    rule.repeat === ScheduleRuleRepeat.Daily &&
    rule.itemReference === ref &&
    isRuleActiveOnDate(rule, dateISO)
  );
  // if no laid-out weekday reads the daily rule, still keep it in line with the week
  const voters = weekdaysOnDailyRule.length > 0 ? weekdaysOnDailyRule : laidOutWeekdays;
  if (dailyRules.length === 0 || voters.length === 0) return;

  // the shared daily rule takes the time most of its readers agree on
  const dailyTime = mostCommonValue(voters.map(weekday => timeByWeekday[weekday]));
  dailyRules.forEach(rule => rule.time_hr = dailyTime);

  // for each weekday reading the daily rule whose own time differs, give it a weekly rule
  for (const weekday of weekdaysOnDailyRule) {
    if (timeByWeekday[weekday] !== dailyTime) {
      rules.push({
        itemType: Schedule_ItemType.Meal,
        repeat: ScheduleRuleRepeat.Weekly,
        weekday,
        itemReference: ref,
        time_hr: timeByWeekday[weekday],
        dateISO,
      });
    }
  }
}

/**
 * The value that occurs most often. On a tie, the one that occurs first — so a list
 * ordered by weekday settles ties towards the earliest weekday.
 */
function mostCommonValue(values: number[]): number {
  let best = values[0];
  let bestCount = 0;
  // for each distinct value, keep it if it occurs more often than the best so far
  for (const value of values) {
    const count = values.filter(other => other === value).length;
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

function consolidateScheduleRules(scheduleRules: ScheduleRule[], ruleStartDateISO: string) {
  void scheduleRules;
  void ruleStartDateISO;

  // Consolidate meal rules: if all non-expired weekly rules for a meal have same time_hr and dateISO, combine into one daily rule
  const mealRules = scheduleRules.filter(rule => rule.itemType === Schedule_ItemType.Meal);
  const mealReferences = Array.from(new Set(mealRules.map(rule => rule.itemReference)));

  for (const ref of mealReferences) {
    const weeklyRules = mealRules.filter(
      rule => rule.repeat === ScheduleRuleRepeat.Weekly &&
        rule.itemReference === ref &&
        !rule.endDateISO
    );
    if (weeklyRules.length === 0) continue;

    const firstTime = weeklyRules[0].time_hr;
    const firstDateISO = weeklyRules[0].dateISO;
    const allSame = weeklyRules.every(rule => rule.time_hr === firstTime && rule.dateISO === firstDateISO);
    // weekly rules on only some weekdays are exceptions to the daily rule, not a
    // replacement for it — merging them would give their time to every weekday
    const coveredWeekdays = new Set(weeklyRules.map(rule => rule.weekday));
    const coversWholeWeek = [1, 2, 3, 4, 5, 6, 7].every(weekday => coveredWeekdays.has(weekday));

    if (allSame && coversWholeWeek) {
      // Remove all weekly rules for this meal reference
      for (const rule of weeklyRules) {
        const idx = scheduleRules.indexOf(rule);
        if (idx > -1) scheduleRules.splice(idx, 1);
      }
      // Add a daily rule
      scheduleRules.push({
        itemType: Schedule_ItemType.Meal,
        repeat: ScheduleRuleRepeat.Daily,
        itemReference: ref,
        time_hr: firstTime,
        dateISO: firstDateISO,
      });
    }
  }
}

export function generateScheduleWithDefaults(declaredState: DeclaredState): Schedule_Day[] {
  let [wakeupHours, bedHours] = getDefaultWakeupAndBedTimeHours(declaredState);
  let mealHours = getDefaultMealTimes(declaredState, wakeupHours, bedHours);
  // all must be rounded to 15 minutes due to the UI on schedule page!!!
  wakeupHours = Math.round(wakeupHours * 4) / 4;
  mealHours = mealHours.map(m => Math.round(m * 4) / 4);
  bedHours = Math.round(bedHours * 4) / 4;

  // place workout between meals 2 and 3, regardless of frequency or IF
  let workoutHours = mealHours[1] + 1;

  let schedule = [];
  for (let iDay = 0; iDay < 14; iDay++) {
    let day = { items: [] };
    schedule[iDay] = day;
    if (declaredState.trainingDays[wrapIntegerToInterval(iDay, 7)]) {
      const mealHours = getDefaultMealTimes(declaredState, wakeupHours, bedHours, workoutHours);
      day.items = generateScheduleDayItems(declaredState, wakeupHours, bedHours, mealHours, workoutHours);
    } else {
      const mealHours = getDefaultMealTimes(declaredState, wakeupHours, bedHours);
      day.items = generateScheduleDayItems(declaredState, wakeupHours, bedHours, mealHours);
    }

    if (iDay === 6) {
      day.items.push({
        type: Schedule_ItemType.WeeklyCheckup,
        // a snapshot, not the source of truth — see Schedule_ItemWeeklyCheckup
        time_hr: getWeeklyCheckupTimeHr(bedHours),
        targets: []
      })
    }
  }

  return schedule;
}

export function generateScheduleFromBacklogEntries(entries: Log_Entry[], today: DateTime): [ scheduleDays: Schedule_Day[], entiresByDay: { [key: string]: Log_Entry[] } ] {
  let scheduleDays: Schedule_Day[] = [];
  let entiresByDay: { [key: string]: Log_Entry[] } = {};
  for (let entry of entries) {
    const entryTime = getEntryLocalTimeInDesiredTimeZone(entry, today);
    const entryDay = entryTime.toISODate();
    if (entryDay < today.toISODate()) {
      // skip entries that are older than today
      continue;
    }
    if (!entiresByDay[entryDay]) {
      entiresByDay[entryDay] = [];
    }
    entiresByDay[entryDay].push(entry);
  }

  let keys = Object.keys(entiresByDay);
  keys.sort()

  // this should fix the issue where end day is after midnight and is overflowing to the next day.
  for (let iDay = 0; iDay < keys.length; iDay++) {
    let day = keys[iDay];
    let nextDay = iDay + 1 < keys.length ? keys[iDay + 1] : undefined;

    if (nextDay && entiresByDay[nextDay][0].type === LogRecordType.DayEnd) {
      entiresByDay[day].push(entiresByDay[nextDay][0]);
      entiresByDay[nextDay].splice(0, 1);
      if (entiresByDay[nextDay].length === 0) {
        delete entiresByDay[nextDay];
        keys.splice(iDay + 1, 1);
      }
    }
  }

  for (let day of keys) {
    let dayItems: Schedule_Item[] = [];

    let dayStartEntry: Log_DayStart = entiresByDay[day].find((e: Log_Entry) => e.type === LogRecordType.DayStart) as Log_DayStart;
    if (dayStartEntry) {
      dayItems.push({
        type: Schedule_ItemType.DayStart,
        time_hr: isoTimeToHours(formatHourMinute24(getEntryLocalTimeInDesiredTimeZone(dayStartEntry, DateTime.fromISO(dayStartEntry.time_utc)))),
        targets: dayStartEntry.targets,
      })
    }

    let dayMeals: Log_Meal[] = entiresByDay[day].filter((e: Log_Entry) => e.type === LogRecordType.Meal) as Log_Meal[];
    for (let mealEntry of dayMeals) {
      dayItems.push({
        type: Schedule_ItemType.Meal,
        time_hr: isoTimeToHours(formatHourMinute24(getEntryLocalTimeInDesiredTimeZone(mealEntry, DateTime.fromISO(mealEntry.time_utc)))),
        targets: mealEntry.targets,
        cutMeal: mealEntry.cutMeal
      })
    }

    let workoutEntry: Log_Workout = entiresByDay[day].find((e: Log_Entry) => e.type === LogRecordType.Workout) as Log_Workout;
    if (workoutEntry) {
      dayItems.push({
        type: Schedule_ItemType.Workout,
        time_hr: isoTimeToHours(formatHourMinute24(getEntryLocalTimeInDesiredTimeZone(workoutEntry, DateTime.fromISO(workoutEntry.time_utc)))),
      })
    }

    let dayEndEntry: Log_DayEnd = entiresByDay[day].find((e: Log_Entry) => e.type === LogRecordType.DayEnd) as Log_DayEnd;
    if (dayEndEntry) {
      dayItems.push({
        type: Schedule_ItemType.DayEnd,
        time_hr: isoTimeToHours(formatHourMinute24(getEntryLocalTimeInDesiredTimeZone(dayEndEntry, DateTime.fromISO(dayEndEntry.time_utc)))),
      })
    } else {
      console.assert(false);
    }

    dayItems.sort((a, b) => a.time_hr - b.time_hr)

    scheduleDays.push({
      items: dayItems,
    })
  }

  return [ scheduleDays, entiresByDay ];
}

/** Diagnostic descriptor for a completed log entry the end-of-day sweep had to rescue
 * (i.e. an entry the per-type handlers dropped that would otherwise have been deleted). Carries
 * no user data — only the id/type/state — so it is safe to send to telemetry. See the sweep in
 * generateScheduleLogEntries and design-docs/sync-integrity.md. */
export interface RescuedLogEntryInfo {
  id: string;
  type: LogRecordType;
  state: LogRecordState;
}

/** Diagnostic descriptor for a fresh log entry the schedule rebuild could not give its natural id,
 * because another entry already holds it (#4641). An entry keeps its id when it is moved, so a
 * moved entry can hold the id a fresh entry on its old time would get. `naturalId` is the id that
 * was taken, `id` the one given instead. Ids only — no user data — so it is safe for telemetry. */
export interface ReissuedLogEntryIdInfo {
  naturalId: string;
  id: string;
  type: LogRecordType;
}

/** Optional out-lists a caller of generateScheduleLogEntries can pass to learn what the rebuild had
 * to repair. Observe-only: filling them never changes the entries returned. Leave a list out when
 * you do not want it. */
export interface ScheduleRebuildDiagnostics {
  /** one descriptor for every completed entry the end-of-day sweep rescued from deletion */
  rescuedCompletedEntries?: RescuedLogEntryInfo[];
  /** one descriptor for every fresh meal that had to be given an id other than its natural one */
  reissuedEntryIds?: ReissuedLogEntryIdInfo[];
  /** the id of every existing meal that no slot took, on a day holding more meals than slots */
  mealIdsLeftOffSlots?: string[];
}

/**
 * Should this entry take the targets the schedule currently holds for its slot?
 *
 * Yes in two cases, and they are there for different reasons:
 *
 * - The entry is today's or a future day's. Its targets are still guidance the user
 *   has yet to act on, so they must follow the current plan. A past day is left
 *   alone: the user ate against the target that was in force at the time, and
 *   rewriting it would falsify their history.
 * - The entry has no targets at all. Something has to fill them in, whatever day it
 *   sits on, or the day renders with no numbers.
 *
 * Every place that keeps an entry on a scheduled slot asks this here, so none of them
 * can answer it differently — the same argument as `logEntryHoldsUserData`, which is in
 * `log-entry-user-data.ts` because the day list needs it too. What
 * it cost when the end-of-day sweep did not ask: `design-docs/sync-integrity.md`, "An
 * entry the sweep moves onto a slot must also take that slot's TARGETS (#4511)".
 */
export function entryTakesScheduledTargets(entry: Log_DayStart | Log_Meal, today_local: DateTime): boolean {
  return getEntryLocalTimeInDesiredTimeZone(entry, today_local).toISODate() >= today_local.toISODate()
    || deepCompareEQ(entry.targets, {});
}

/**
 * Should this meal take the goal the schedule currently holds for its slot?
 *
 * The same answer as `entryTakesScheduledTargets`, with one more exception: a meal the user
 * ticked Done keeps the goal it had when it was ticked off, even on today (owner decision,
 * #3976). The user ate against that goal, and a re-plan later the same day would otherwise
 * turn an on-target meal into an over-target one. A Done meal with no goal at all still takes
 * the slot's, because something has to fill it in.
 */
export function mealTakesScheduledTargets(meal: Log_Meal, today_local: DateTime): boolean {
  if (!meal.targets || deepCompareEQ(meal.targets, {})) {
    return true;
  }
  return meal.state !== LogRecordState.Done && entryTakesScheduledTargets(meal, today_local);
}

/** A meal that keeps its own goal, beside the goal the schedule now gives its slot (none when
 *  the solve left the slot without one). */
export interface KeptMealTargets {
  kept: NutrientTargets;
  scheduled?: NutrientTargets;
}

/**
 * The day nutrients whose day figure is the sum of the meal figures (see plan-update's
 * `targetSum`). Fat and net carbs are sums only when fixed macros give the day a `target` for
 * them; otherwise the day holds a daily minimum there, which is not a sum. Fiber and
 * cholesterol are daily figures and are never listed.
 */
const DAY_NUTRIENTS_SUMMED_FROM_MEALS = [INFOODS.Calories, INFOODS.Protein];
const DAY_NUTRIENTS_SUMMED_WHEN_TARGETED = [INFOODS.Fat, INFOODS.CarbNet];

/** The amount a goal stands for: its target where it has one, else its minimum. */
function goalAmount(goal: NutrientTarget | undefined): number | undefined {
  return goal?.target ?? goal?.min;
}

/**
 * The day's goal once the meals that keep their own goal are counted (#3976).
 *
 * The schedule's day goal is the sum of the goals it gives the day's meal slots. When a Done
 * meal keeps a different goal from the one its slot now has, the day goal moves by the
 * difference, so the ring at the top of the day always equals the meal cards below it. A calorie
 * target moves and its min/max are rescaled so they keep the day's own margin; a goal that holds
 * only a minimum moves that minimum.
 *
 * Answers `dayTargets` itself when no meal moves it, so an unchanged day keeps comparing equal to
 * the object the schedule holds.
 */
export function dayTargetsWithKeptMeals(dayTargets: NutrientTargets | undefined, keptMeals: KeptMealTargets[]): NutrientTargets | undefined {
  if (!dayTargets || keptMeals.length === 0) {
    return dayTargets;
  }
  const adjusted = deepClone(dayTargets);
  const nutrients = [
    ...DAY_NUTRIENTS_SUMMED_FROM_MEALS,
    ...DAY_NUTRIENTS_SUMMED_WHEN_TARGETED.filter(n => adjusted[n]?.target !== undefined),
  ];
  for (const nutrient of nutrients) {
    const dayGoal = adjusted[nutrient];
    if (!dayGoal) continue;
    let difference = 0;
    for (const meal of keptMeals) {
      const kept = goalAmount(meal.kept[nutrient]);
      const scheduled = goalAmount(meal.scheduled?.[nutrient]);
      // if either side lacks this nutrient there is no difference to add, only a guess
      if (!Number.isFinite(kept) || !Number.isFinite(scheduled)) continue;
      difference += kept - scheduled;
    }
    moveDayGoal(dayGoal, difference);
  }
  return adjusted;
}

/**
 * Move one day goal by `difference`, in place. A goal with a target moves the target and
 * rescales its min/max so they keep the day's own margin; a goal that holds only a minimum
 * moves that minimum.
 */
function moveDayGoal(dayGoal: NutrientTarget, difference: number) {
  if (difference === 0) return;
  if (dayGoal.target === undefined) {
    if (dayGoal.min !== undefined) dayGoal.min += difference;
    return;
  }
  const newTarget = dayGoal.target + difference;
  // a zero target has no margin to keep, so its min/max stay as they are
  if (dayGoal.target !== 0) {
    const scale = newTarget / dayGoal.target;
    if (dayGoal.min !== undefined) dayGoal.min = Math.round(dayGoal.min * scale);
    if (dayGoal.max !== undefined) dayGoal.max = Math.round(dayGoal.max * scale);
  }
  dayGoal.target = newTarget;
}

/**
 * Choose which of a day's existing meals goes on each of its meal slots (#4641). `dayMeals` must
 * be sorted earliest first. Each meal is used for at most one slot, and the meals keep their time
 * order across the slots wherever a free slot allows it. A slot answered with `undefined` gets a
 * fresh meal.
 *
 *   - as many meals as slots: the i-th meal goes on the i-th slot;
 *   - otherwise: each slot first takes a meal sitting exactly on its time. Each run of slots still
 *     empty then takes, in order, the meals lying between the matched meals on either side of it.
 *     Any meal still unplaced then takes any slot still empty, even out of time order. Only when
 *     there are more meals than slots is a meal left unplaced.
 *
 * The reasons, and the customer day that needed them: design-docs/sync-integrity.md, "A Fresh
 * Entry Must Never Take an Id Another Entry Holds".
 */
export function pairMealsWithSlots(
  dayMeals: Log_Meal[],
  slotCount: number,
  isOnSlot: (meal: Log_Meal, iSlot: number) => boolean,
): (Log_Meal | undefined)[] {
  if (dayMeals.length === slotCount) {
    return [...dayMeals];
  }

  // for each slot, the index in dayMeals of the meal it takes, or -1 while it has none
  const mealIndexForSlot: number[] = new Array(slotCount).fill(-1);
  const isUsed: boolean[] = new Array(dayMeals.length).fill(false);
  // first each slot takes the earliest unused meal sitting exactly on its time
  for (let iSlot = 0; iSlot < slotCount; ++iSlot) {
    const iMeal = dayMeals.findIndex((meal, i) => !isUsed[i] && isOnSlot(meal, iSlot));
    if (iMeal !== -1) {
      mealIndexForSlot[iSlot] = iMeal;
      isUsed[iMeal] = true;
    }
  }

  // then fill each run of empty slots with the meals between the matched meals around it
  let iMealBeforeRun = -1;
  let iSlot = 0;
  while (iSlot < slotCount) {
    if (mealIndexForSlot[iSlot] !== -1) {
      iMealBeforeRun = mealIndexForSlot[iSlot];
      ++iSlot;
      continue;
    }
    let iRunEnd = iSlot;
    while (iRunEnd < slotCount && mealIndexForSlot[iRunEnd] === -1) {
      ++iRunEnd;
    }
    const iMealAfterRun = iRunEnd < slotCount ? mealIndexForSlot[iRunEnd] : dayMeals.length;
    for (let iMeal = iMealBeforeRun + 1; iMeal < iMealAfterRun && iSlot < iRunEnd; ++iMeal) {
      // if slot times were ever out of order, a meal in this range could already sit on a slot
      if (isUsed[iMeal]) continue;
      mealIndexForSlot[iSlot] = iMeal;
      isUsed[iMeal] = true;
      ++iSlot;
    }
    iSlot = iRunEnd;
  }

  // last, any meal still unplaced takes a slot still empty, in order. this can put a meal out of
  // time order, but a meal left off every slot would keep a name a slot meal now holds, and the
  // day screen shows one meal per name, so one of the two would be hidden with its food
  let iUnplacedMeal = 0;
  for (let iEmptySlot = 0; iEmptySlot < slotCount; ++iEmptySlot) {
    if (mealIndexForSlot[iEmptySlot] !== -1) continue;
    while (iUnplacedMeal < dayMeals.length && isUsed[iUnplacedMeal]) {
      ++iUnplacedMeal;
    }
    if (iUnplacedMeal === dayMeals.length) break;
    mealIndexForSlot[iEmptySlot] = iUnplacedMeal;
    isUsed[iUnplacedMeal] = true;
  }

  return mealIndexForSlot.map(iMeal => iMeal === -1 ? undefined : dayMeals[iMeal]);
}

/**
 * Rename a meal no slot took, when a slot meal holding data is about to carry the same name (#4641).
 *
 * Such a meal (there were more meals than slots) is left to the end-of-day sweep, which keeps it if
 * it holds food but never renames it. The day screen and the day total show one meal per name and
 * prefer the one holding data (`pickOneMealPerSlot`), so two meals holding data under one name
 * would hide one of them with its food. A clash with an EMPTY slot meal is left alone on purpose:
 * there the screen already shows the meal holding food, as one card. The new name is the first
 * "Meal N" past the slot names that no other meal of the day uses. Slot meals are named
 * "Meal <slot number>", as `handleMeals` names them.
 */
function renameMealsLeftOffSlots(dayMeals: Log_Meal[], mealForSlot: (Log_Meal | undefined)[]) {
  const slotNamesHoldingData = new Set<string>();
  for (let iSlot = 0; iSlot < mealForSlot.length; ++iSlot) {
    const slotMeal = mealForSlot[iSlot];
    if (slotMeal && logEntryHoldsUserData(slotMeal)) {
      slotNamesHoldingData.add(`Meal ${iSlot + 1}`);
    }
  }
  const namesInUse = new Set(dayMeals.map(meal => meal.name));
  let nextNumber = mealForSlot.length + 1;
  for (const meal of dayMeals) {
    if (mealForSlot.includes(meal) || !slotNamesHoldingData.has(meal.name)) continue;
    while (namesInUse.has(`Meal ${nextNumber}`)) {
      ++nextNumber;
    }
    meal.name = `Meal ${nextNumber}`;
    namesInUse.add(meal.name);
  }
}

export function generateScheduleLogEntries(
  schedule: Schedule_Day[],
  startDate_local: DateTime,
  endDate_local: DateTime,
  logs: Log_Entry[],
  fitnessPlan: FitnessPlan,
  today_local: DateTime,
  trackErrorCallback: (error: string) => void,
  // optional out-lists, so the (client) caller can surface repairs to Sentry. Observe-only.
  diagnostics?: ScheduleRebuildDiagnostics,
): Log_Entry[] {
  // validate that endDate_local is at end of day, not start of day
  if (endDate_local.hour === 0 && endDate_local.minute === 0 && endDate_local.second === 0 && endDate_local.millisecond === 0) {
    trackErrorCallback(`generateScheduleLogEntries called with endDate_local at start of day (${endDate_local.toISO()}) instead of end of day. This will cause entries on the last day to be missing buckets.`);
  }

  // planReference is no longer pre-computed here - it's calculated on-demand
  // via getEffectivePlanReference when workouts are accessed

  let entries = [];
  let entiresByDay_local: { [key: string]: Log_Entry[] } = {};
  for (
    let date_local = startDate_local;
    date_local < endDate_local;
    date_local = date_local.plus({ days: 1 }))
  {
    let day_local = date_local.toISODate();
    entiresByDay_local[day_local] = [];
  }

  // track diagnostic info for out-of-range entries
  let outOfRangeEntries: any[] = [];

  for (let entry of logs) {
    const entryTime = getEntryLocalTimeInDesiredTimeZone(entry, today_local);
    const entryDay = entryTime.toISODate();
    if (entryTime < startDate_local.startOf('day')) {
      delete entry._deleted;
    } else {
      if (!entiresByDay_local[entryDay]) {
        // collect diagnostic info (but don't log yet)
        const diagnosticInfo = {
          entryId: entry._id,
          entryDate: entryDay,
          entryTimeUtc: entry.time_utc,
          entryTimeOffsetMin: entry.timeOffset_min,
          computedEntryTime: entryTime.toISO(),
          entryType: entry.type,
          entryState: entry.state,
          scheduleRangeStart: startDate_local.toISODate(),
          scheduleRangeEnd: endDate_local.toISODate(),
          todayLocal: today_local.toISODate(),
          todayLocalTimezone: today_local.zoneName,
          fullEntry: entry
        };
        outOfRangeEntries.push(diagnosticInfo);
        entiresByDay_local[entryDay] = [];
      }
      entiresByDay_local[entryDay].push(entry);
    }
  }

  // filter out expected cases:
  // 1. Entries already marked _deleted (expected cleanup)
  // 2. Entries whose local time IS within schedule range (timezone edge case where bucket check failed)
  const unexpectedOutOfRange = outOfRangeEntries.filter(e => {
    if (e.fullEntry._deleted) return false;
    // check if entry's local time is actually within the schedule range
    const entryLocalTime = DateTime.fromISO(e.computedEntryTime);
    const isWithinRange = entryLocalTime >= startDate_local.startOf('day') && entryLocalTime < endDate_local;
    if (isWithinRange) return false; // timezone edge case, not an error
    return true;
  });

  // only log genuinely unexpected out-of-range entries
  if (unexpectedOutOfRange.length > 0) {
    const bucketDates = Object.keys(entiresByDay_local).sort();
    const summary = {
      totalLogsProcessed: logs.length,
      totalBucketsCreated: bucketDates.length,
      bucketDateRange: bucketDates.length > 0 ? `${bucketDates[0]} to ${bucketDates[bucketDates.length - 1]}` : 'none',
      outOfRangeCount: unexpectedOutOfRange.length,
      outOfRangeEntries: unexpectedOutOfRange,
      scheduleDaysCount: schedule.length
    };
    trackErrorCallback(`Schedule range validation summary: ${JSON.stringify(summary, null, 2)}`);

    // now log each individual entry
    for (const diagnosticInfo of unexpectedOutOfRange) {
      trackErrorCallback(`Log entry ${diagnosticInfo.entryId} has date ${diagnosticInfo.entryDate} which is not in the schedule range [${diagnosticInfo.scheduleRangeStart}, ${diagnosticInfo.scheduleRangeEnd})! Entry time_utc: ${diagnosticInfo.entryTimeUtc}, timeOffset_min: ${diagnosticInfo.entryTimeOffsetMin}, computed entryTime: ${diagnosticInfo.computedEntryTime}. Diagnostic data: ${JSON.stringify(diagnosticInfo)}`);
    }
  }

  let keys_local = Object.keys(entiresByDay_local);
  keys_local = keys_local.sort();

  // for all the days
  // build a date→index map from the schedule range so calendar days always map to the
  // correct schedule day regardless of extra buckets from out-of-range entries
  const scheduleDateMap = new Map<string, number>();
  for (let d = startDate_local, i = 0; i < schedule.length; d = d.plus({ days: 1 }), i++) {
    scheduleDateMap.set(d.toISODate(), i);
  }

  // every id already in use: the whole input, plus each fresh entry as it is kept below
  const takenIds = new Set(logs.map(e => e._id));

  /**
   * Make sure a freshly built entry does not take an id another entry holds: a moved entry keeps
   * the id of its old time, and two entries with one id lose one of them (#4641). See
   * firstFreeLogEntryId for how the free id is chosen.
   */
  const giveFreshEntryAFreeId = (entry: Log_Entry) => {
    const naturalId = entry._id;
    entry._id = firstFreeLogEntryId(entry.time_utc, entry.type, takenIds);
    takenIds.add(entry._id);
    if (entry._id !== naturalId) {
      diagnostics?.reissuedEntryIds?.push({ naturalId, id: entry._id, type: entry.type });
    }
  }

  for (let iDay = 0; iDay < keys_local.length; ++iDay) {
    const dayLogEntries = entiresByDay_local[keys_local[iDay]];
    const date_local = DateTime.fromISO(keys_local[iDay]);
    const scheduleDayIndex = scheduleDateMap.get(keys_local[iDay]);
    // skip days outside the schedule range (extra buckets from out-of-range entries)
    if (scheduleDayIndex === undefined) continue;
    const scheduleDay = schedule[scheduleDayIndex];

    // read here rather than inside handleDayStart: the end-of-day sweep needs it too,
    // to give the day's targets to a day start it moves onto this slot (#4511)
    const dayStartItem = scheduleDay.items.find(e => e.type === Schedule_ItemType.DayStart) as Schedule_ItemDayStart;

    // this day's meal slots, and which existing meal goes on each (`pairMealsWithSlots`). worked
    // out before the day start is built, because the day's goal depends on which meals keep
    // their own goal (#3976). nothing between here and handleMeals touches a meal.
    const mealItems = scheduleDay.items.filter(e => e.type === Schedule_ItemType.Meal) as Schedule_ItemMeal[];
    const slotTimes_local: DateTime[] = [];
    for (const item of mealItems) {
      if (!item.time_hr) {
        item.time_hr = 0;
      }
      if (item.time_hr < 0) {
        item.time_hr = wrapIntegerToInterval(item.time_hr, 24);
      }
      slotTimes_local.push(setDecimalHour(date_local, item.time_hr));
    }
    const isMealOnSlot = (meal: Log_Meal, iSlot: number) =>
      getEntryLocalTimeInDesiredTimeZone(meal, slotTimes_local[iSlot]).toISO() === slotTimes_local[iSlot].toISO();

    // this day's meals, earliest first (by the time the user sees; the id breaks a tie). the
    // caller already hands them over sorted, but the pairing must not depend on that.
    const localMillis = (meal: Log_Meal) => getEntryLocalTimeInDesiredTimeZone(meal, date_local).toMillis();
    const dayMeals = (dayLogEntries.filter(e => e.type === LogRecordType.Meal) as Log_Meal[])
      .sort((a, b) => localMillis(a) - localMillis(b) || a._id.localeCompare(b._id));

    const mealForSlot = pairMealsWithSlots(dayMeals, mealItems.length, isMealOnSlot);

    // the goal this day's day start takes: the schedule's, moved by every meal on a slot that
    // keeps its own goal, so the day adds up its meals (#3976). only a day whose goals still
    // follow the plan has such a meal; a past day keeps what it holds either way.
    const keptMealTargets: KeptMealTargets[] = [];
    mealForSlot.forEach((meal, iSlot) => {
      if (meal && !mealTakesScheduledTargets(meal, today_local) && entryTakesScheduledTargets(meal, today_local)) {
        keptMealTargets.push({ kept: meal.targets, scheduled: mealItems[iSlot].targets });
      }
    });
    const dayTargets = dayTargetsWithKeptMeals(dayStartItem?.targets, keptMealTargets);

    /**
     * Which of this day's entries of a one-per-day `type` the handler keeps for the slot at
     * `slotTime_local`, and whether it already sits on that slot. Undefined when the day has none.
     *
     * When any of them holds user data, the one on the slot is kept, otherwise the first one found;
     * the sweep below then keeps every other one holding data, so none of those can be lost, and
     * none of them is moved by this choice.
     *
     * When none of them does, the others are deleted, so the choice is the one every device and
     * every screen makes (`pickOneEntryPerSingletonSlot`), with the slot left out of it. Why: see
     * design-docs/sync-integrity.md, "A wake-up or bedtime entry the app ticked `Done` holds no
     * user data (#6431)".
     */
    const chooseEntryForSlot = <T extends Log_Entry>(type: LogRecordType, slotTime_local: DateTime): { entry: T, isOnSlot: boolean } | undefined => {
      const isOnSlot = (e: Log_Entry) => getEntryLocalTimeInDesiredTimeZone(e, slotTime_local).toISO() === slotTime_local.toISO();
      const candidates = dayLogEntries.filter(e => e.type === type);
      if (candidates.length === 0) return undefined;
      const entry = candidates.some(logEntryHoldsUserData)
        ? candidates.find(isOnSlot) ?? candidates[0]
        : pickOneEntryPerSingletonSlot(candidates, slotTime_local).kept[0];
      return { entry: entry as T, isOnSlot: isOnSlot(entry) };
    }

    /**
     * Put this day's one scheduled entry of `type` onto `slotTime_local`, keeping whatever
     * the user already has rather than replacing it.
     *
     * Three cases, in the order they are tried:
     *   - the entry `chooseEntryForSlot` picks already sits exactly on the slot: keep it there;
     *   - it sits somewhere else on this day: keep THAT document (so its `_rev` survives) and
     *     move it onto the slot;
     *   - there is none: keep the freshly built one.
     * In every case the entry is un-deleted, because the caller pre-marks every entry
     * `_deleted` and only the ones returned here are revived.
     *
     * This is the shape the day start, the weigh-in, the day end and the weekly check-up all
     * share. What stays with each caller is what genuinely differs between them: how the slot
     * time is worked out (the weekly check-up's day-crossing guard is one such derivation, and
     * belongs to the check-up alone), which create function builds a fresh entry, and any
     * scheduled fields to copy onto whichever entry is kept.
     *
     * `applyScheduledFields` is handed the entry that will be kept and told whether it was
     * already on the slot, because the day start answers those two cases differently. The
     * workout and meal handlers below do NOT use this: a workout has a whole extra arm for
     * days with no workout scheduled, and meals are many per day and matched by position.
     */
    const upsertScheduledEntry = <T extends Log_Entry>(
      type: LogRecordType,
      slotTime_local: DateTime,
      createEntry: (slotTime_local: DateTime) => T,
      applyScheduledFields?: (entry: T, isOnSlot: boolean) => void,
    ) => {
      const newEntry = createEntry(slotTime_local);
      const chosen = chooseEntryForSlot<T>(type, slotTime_local);
      if (chosen?.isOnSlot) {
        const entryOnSlot = chosen.entry;
        applyScheduledFields?.(entryOnSlot, true);
        delete entryOnSlot._deleted;
        entries.push(entryOnSlot);
        return;
      }
      if (chosen) {
        const entryElsewhereToday = chosen.entry;
        // Use existing entry to preserve _rev from database - only update time, plus
        // whatever scheduled fields the caller writes
        copyEntryLocalTime(entryElsewhereToday, newEntry);
        applyScheduledFields?.(entryElsewhereToday, false);
        delete entryElsewhereToday._deleted;
        entries.push(entryElsewhereToday);
        return;
      }
      // No existing entry - use the new one
      applyScheduledFields?.(newEntry, false);
      entries.push(newEntry);
    }

    const handleDayStart = () => {
      if (dayStartItem) {
        const itemTime_local = setDecimalHour(date_local, dayStartItem.time_hr);
        upsertScheduledEntry(LogRecordType.DayStart, itemTime_local, createDayStartEntry,
          (dayStartEntry, isOnSlot) => {
            // an entry already on the slot keeps the targets object it holds when the two are
            // deeply equal, so an unchanged plan does not swap the stored object for the
            // schedule's one. off the slot there is nothing to compare against yet.
            if (isOnSlot && deepCompareEQ(dayTargets, dayStartEntry.targets)) return;
            if (entryTakesScheduledTargets(dayStartEntry, today_local)) {
              dayStartEntry.targets = dayTargets;
            }
          });

        const weighInTime_local = itemTime_local.plus({ minutes: 5 });
        upsertScheduledEntry(LogRecordType.WeighIn, weighInTime_local,
          slotTime_local => createGenericEntry(slotTime_local, LogRecordType.WeighIn) as Log_WeighIn);
      }
    }

    const handleDayEnd = () => {

      const dayEndItem = scheduleDay.items.find(e => e.type === Schedule_ItemType.DayEnd) as Schedule_ItemDayEnd;
      if (dayEndItem) {
        const itemTime_local = setDecimalHour(date_local, dayEndItem.time_hr);
        // create entry on appropriate hour
        upsertScheduledEntry(LogRecordType.DayEnd, itemTime_local,
          slotTime_local => createGenericEntry(slotTime_local, LogRecordType.DayEnd, '') as Log_DayEnd);


        // the item is fetched only to learn WHETHER a check-up is scheduled this day. its
        // own time_hr is deliberately not read, and wiring it in would be worse than it
        // looks, in two ways that are not visible from here:
        //   - the branch inside upsertScheduledEntry that reuses a check-up entry found
        //     elsewhere on this day matches on the type alone, not on the entry's state, and
        //     calls copyEntryLocalTime on it. it would therefore move check-ups customers
        //     have already COMPLETED, silently editing finished history.
        //   - reminders are suppressed by an id built from the entry's own weekday and time
        //     (buildEntrySuppressionKey in client/src/services/notification-builder.ts, which
        //     composes `${type}-${weekday}-${HH:MM}`). moving the entry invalidates every
        //     existing suppression, so every customer gets one spurious check-up reminder.
        // so the time comes from the day-end entry instead. see Schedule_ItemWeeklyCheckup.
        const weeklyCheckupItem = scheduleDay.items.find(e => e.type === Schedule_ItemType.WeeklyCheckup) as Schedule_ItemWeeklyCheckup;
        if (weeklyCheckupItem) {
          let weeklyCheckupTime_local = itemTime_local.minus({ minutes: WEEKLY_CHECKUP_MINUTES_BEFORE_DAY_END });
          // the check-up belongs to the day it is scheduled on. if the user's day ends at
          // midnight (or in the ten minutes just after it), taking 10 minutes off lands this
          // on the day BEFORE, which has no check-up scheduled. every entry is filed under the
          // local calendar day its time falls on, so the next rebuild would not find it there,
          // would create a fresh unfinished check-up on the real day, and would give that new
          // entry the same document id - overwriting the completed one. that is #4424: a
          // finished check-up kept coming back as unfinished. add the day back, which puts the
          // entry at the far end of the day it belongs to.
          if (weeklyCheckupTime_local < date_local.startOf('day')) {
            weeklyCheckupTime_local = weeklyCheckupTime_local.plus({ days: 1 });
          }
          upsertScheduledEntry(LogRecordType.WeeklyCheckup, weeklyCheckupTime_local,
            createWeeklyCheckupEntry);
        }
      }
    }

    const handleWorkout = () => {
      const workoutItem = scheduleDay.items.find(e => e.type === Schedule_ItemType.Workout) as Schedule_ItemWorkout;
      if (workoutItem) {
        if (!workoutItem.time_hr) {
          workoutItem.time_hr = 0;
        }
        const itemTime_local = setDecimalHour(date_local, workoutItem.time_hr);
        // if the training program is not ready yet
        if (fitnessPlan.workouts.length === 0) {
          // don't attempt to add workouts
          return;
        }

        // the existing workout to keep, if any: the same choice the other one-per-day events make
        const chosen = chooseEntryForSlot<Log_Workout>(LogRecordType.Workout, itemTime_local);
        if (chosen?.isOnSlot) {
          // use existing workout as-is (planReference computed on-demand if not set)
          delete chosen.entry._deleted;
          entries.push(chosen.entry);
          return;
        }

        // an existing workout at another time on this day
        const existingWorkoutLog = chosen?.entry;
        if (existingWorkoutLog) {
          // Use existing entry to preserve _rev from database - update time to match schedule
          setEntryLocalTime(existingWorkoutLog, itemTime_local);
          delete existingWorkoutLog._deleted;
          entries.push(existingWorkoutLog);
          return;
        }

        // no existing workout - create a stub (planReference computed on-demand)
        const workoutLog = createWorkoutEntry(itemTime_local);
        entries.push(workoutLog);
      } else {
        // no scheduled workout for this day, but check for existing workout log
        const workoutEntry = dayLogEntries.find(e => e.type === LogRecordType.Workout) as Log_Workout;
        // preserve an existing workout the user actually did something with (started manually on
        // a non-workout day); an empty placeholder is removed when the schedule changes.
        // `logEntryHoldsUserData` is what decides that, and #4840 is why it asks the workout's
        // content rather than only its state field.
        if (workoutEntry && logEntryHoldsUserData(workoutEntry)) {
          delete workoutEntry._deleted;
          entries.push(workoutEntry);
        }
      }
    }

    /**
     * Put one meal entry on each of this day's meal slots, keeping the entries the user already
     * has (so their `_rev` and food survive) and building a fresh one only for a slot left empty.
     * Which meal goes on which slot is `pairMealsWithSlots`. Content moves with the entry and the
     * targets stay with the slot (#2812); a meal no slot takes is left to the sweep below.
     */
    const handleMeals = () => {
      renameMealsLeftOffSlots(dayMeals, mealForSlot);
      // for each meal no slot took, report its id
      for (const meal of dayMeals) {
        if (!mealForSlot.includes(meal)) {
          diagnostics?.mealIdsLeftOffSlots?.push(meal._id);
        }
      }

      for (let iSlot = 0; iSlot < mealItems.length; ++iSlot) {
        const item = mealItems[iSlot];
        const name = `Meal ${iSlot + 1}`;
        let meal = mealForSlot[iSlot];
        if (meal) {
          // if the meal is off its slot, move it (both time fields together, #4728)
          if (!isMealOnSlot(meal, iSlot)) {
            setEntryLocalTime(meal, slotTimes_local[iSlot]);
          }
        } else {
          meal = createMealEntry(slotTimes_local[iSlot], name);
          giveFreshEntryAFreeId(meal);
        }
        meal.name = name;
        // a meal ticked Done keeps the goal it was eaten against; the day start's goal already
        // counts it (dayTargets above, #3976)
        if (mealTakesScheduledTargets(meal, today_local)) {
          meal.targets = item.targets;
          meal.cutMeal = item.cutMeal;
        }
        delete meal._deleted;
        entries.push(meal);
      }
    }

    if (scheduleDay.items.length === 0) {
      // no scheduled items for this day - preserve any existing log entries as-is
      for (let logEntry of dayLogEntries) {
        delete logEntry._deleted;
        entries.push(logEntry);
      }
      continue;
    }
    const dayEntriesStart = entries.length;
    handleDayStart();
    handleDayEnd();
    handleWorkout();
    handleMeals();

    // FLOOR (never delete completed data) + one-per-day dedup for the singleton events. See
    // design-docs/sync-integrity.md "Schedule Regeneration Must Not Delete Completed Log
    // Entries". The per-type handlers above keep the entry at the exact scheduled time, which
    // after a reschedule (or on a device rebuilding against a stale schedule) can be an empty
    // placeholder while the user's real (data-bearing) entry sits at a different time. The
    // caller pre-marks every entry _deleted and only revives the ones returned here, so a
    // data-bearing entry the handlers did not return would be persisted as a deletion and its
    // logged data lost. For each data-bearing entry not already kept this day, never let it be
    // dropped:
    //   - Singleton events (WeighIn / DayStart / DayEnd / WeeklyCheckup / Workout — exactly one
    //     per day): if an empty placeholder occupies that day's slot, replace it with the
    //     data-bearing entry moved onto the slot (edits follow the reschedule); else preserve.
    //     The slot is keyed by `type` alone — safe, since there is only one of each per day.
    //   - Meals (many per day, and with NO stable per-slot identifier on the log entry — see
    //     Log_Meal, which carries only a `name` and a time-derived _id): do NOT try to match a
    //     meal to a placeholder. Name/position matching is unreliable and could misplace food.
    //     Just preserve the data-bearing meal so its food is never lost; pairing meals with
    //     slots on reschedule is handleMeals' job (see its comment), and the display layer
    //     collapses to one card per meal slot.
    // Empty placeholders are never preserved here, so off-schedule / duplicate placeholders are
    // still cleaned up.
    // a wake-up or bedtime entry the app ticked Done holds nothing the user entered (#6431), so a
    // second copy of one is dropped below like any placeholder. But such an entry is still the
    // record of a past day's targets, so when the handlers kept NO entry of its type this day (a
    // day whose schedule has no wake-up), one of them is kept: the one every screen shows, so
    // every device keeps the same one. not when one of that type holds user data (a skipped
    // one): the loop below keeps that one, and keeping a ticked one too would hand it the slot
    // the skipped one takes, or not, depending on the order the device holds them in.
    const loneTickedDayBoundaryEntries = new Set<Log_Entry>();
    for (const type of [LogRecordType.DayStart, LogRecordType.DayEnd]) {
      const keptOne = entries.slice(dayEntriesStart).some(e => e.type === type);
      const ofType = dayLogEntries.filter(e => e.type === type);
      const ticked = ofType.filter(e => e.state === LogRecordState.Done);
      if (!keptOne && ticked.length > 0 && !ofType.some(logEntryHoldsUserData)) {
        loneTickedDayBoundaryEntries.add(pickOneEntryPerSingletonSlot(ticked, date_local).kept[0]);
      }
    }
    for (let logEntry of dayLogEntries) {
      if (loneTickedDayBoundaryEntries.has(logEntry)) {
        diagnostics?.rescuedCompletedEntries?.push({ id: logEntry._id, type: logEntry.type, state: logEntry.state });
        delete logEntry._deleted;
        entries.push(logEntry);
        continue;
      }
      // an entry holding nothing the user put there is an empty placeholder — safe to drop
      // (deleted as before). Asked through the same helper the workout handler above uses, so
      // the two can never disagree about what "holds user data" means.
      if (!logEntryHoldsUserData(logEntry)) continue;
      const isMeal = logEntry.type === LogRecordType.Meal;
      // scan only this day's kept entries: was this entry already kept, and (singletons only)
      // is there a kept entry for the same one-per-day event slot?
      let alreadyKept = false;
      let keptSlotIndex = -1;
      for (let i = dayEntriesStart; i < entries.length; i++) {
        if (entries[i] === logEntry) { alreadyKept = true; break; }
        if (!isMeal && keptSlotIndex === -1 && entries[i].type === logEntry.type) keptSlotIndex = i;
      }
      if (alreadyKept) continue;
      // this entry holds user data and the handlers did not return it, so without the sweep it
      // would be persisted as a deletion — record the rescue for telemetry (no user data, just
      // id/type/state). Both branches below keep it, so it is always a rescue at this point.
      diagnostics?.rescuedCompletedEntries?.push({ id: logEntry._id, type: logEntry.type, state: logEntry.state });
      if (keptSlotIndex !== -1 && !logEntryHoldsUserData(entries[keptSlotIndex])) {
        // a singleton event's slot is occupied by an empty placeholder — prefer the
        // data-bearing entry, moved onto the scheduled slot so its edits follow the reschedule.
        // "Is it an empty placeholder?" is asked through the same helper as everywhere else in
        // this function, because reading the state field alone answers wrongly for exactly the
        // shape #4840 is about: a workout that says `Scheduled` while holding sets the user
        // finished. This branch DISCARDS whatever sits on the slot, so answering wrongly here
        // drops a finished session out of the rebuilt day with nothing reporting it — the rescue
        // recorded just above names the entry that survives, never the one displaced.
        copyEntryLocalTime(logEntry, entries[keptSlotIndex]);
        delete logEntry._deleted;
        entries[keptSlotIndex] = logEntry;
        // this entry is now THIS day's day start, so it must carry this day's targets —
        // the same ones the handler would have given the placeholder it just replaced.
        // Without this it keeps whatever it was holding, which is a target from before
        // the last plan change, and the day's total then contradicts the meals below it
        // for as long as the entry survives (#4511). Asked through the same helper as
        // the handlers, so the two cannot answer differently. Only the day start carries
        // targets among the one-per-day events, and meals never reach this branch.
        if (logEntry.type === LogRecordType.DayStart && dayStartItem
            && entryTakesScheduledTargets(logEntry as Log_DayStart, today_local)) {
          (logEntry as Log_DayStart).targets = dayTargets;
        }
      } else {
        // a meal (always), or a singleton with no kept placeholder for its slot / a kept
        // entry that also holds data (rare true duplicate) — never delete data, so preserve it
        delete logEntry._deleted;
        entries.push(logEntry);
      }
    }
  }

  return entries;
}

/** One entry `keepOneEntryPerId` dropped because another entry had the same id. Ids only. */
export interface DroppedDuplicateIdInfo {
  id: string;
  type: LogRecordType;
  // whether the entry that was kept instead holds user data
  keptHoldsUserData: boolean;
}

/**
 * Make sure a list of log entries holds at most one entry per `_id`.
 *
 * The app keeps one in-memory object per id, so when a schedule rebuild returns two different
 * entries with one id, the second is written over the first — and if the first held the user's
 * food, that food is gone from the screen and may later be saved away (#4641). The rebuild is
 * meant never to do that; this is the check that it did not. For each id it keeps the entry that
 * holds user data (the first one when both or neither do), and reports every other entry.
 * The same object listed twice is simply kept once, since nothing is overwritten.
 */
export function keepOneEntryPerId(entries: Log_Entry[]): { entries: Log_Entry[], dropped: DroppedDuplicateIdInfo[] } {
  const keptById = new Map<string, Log_Entry>();
  const dropped: DroppedDuplicateIdInfo[] = [];
  for (const entry of entries) {
    const kept = keptById.get(entry._id);
    if (!kept) {
      keptById.set(entry._id, entry);
      continue;
    }
    if (kept === entry) continue;
    // if only the newcomer holds user data, it takes the place of the one kept so far
    const keepNewcomer = !logEntryHoldsUserData(kept) && logEntryHoldsUserData(entry);
    const winner = keepNewcomer ? entry : kept;
    if (keepNewcomer) {
      keptById.set(entry._id, entry);
    }
    dropped.push({ id: entry._id, type: entry.type, keptHoldsUserData: logEntryHoldsUserData(winner) });
  }
  return { entries: [...keptById.values()], dropped };
}

export interface ScheduleParams {
  unavailable: string;
  expanded: boolean;
  workouts: string;
  meals: string;
  wakeup: string;
  sleep: string;
}

/**
 * The time an item happens at when it is the same on every weekday, going by the rules
 * in force on `todayISO`; `0` when the weekdays differ or any weekday has no rule in
 * force. `0` is what the summary below shows as "different times".
 *
 * Each weekday is asked through `resolveRecurringRule`, so a rule that has been ended
 * never counts, and the answer does not depend on the order the rules are stored in.
 */
function getSameTimeEveryWeekday(rules: ScheduleRule[], itemType: Schedule_ItemType, todayISO: string, itemReference?: number): number {
  let sameTime: number | undefined = undefined;
  // for each weekday, if its time in force differs from the others (or it has none), the days differ
  for (let weekday = 1; weekday <= 7; ++weekday) {
    const time = resolveRecurringRule(rules, itemType, todayISO, weekday, itemReference)?.time_hr;
    if (time === undefined || (sameTime !== undefined && time !== sameTime)) {
      return 0;
    }
    sameTime = time;
  }
  return sameTime;
}

/**
 * The meal slots a user still has, as their `itemReference` values in ascending order.
 *
 * A slot counts when at least one weekday has a meal rule in force for it on `todayISO`.
 * Lowering the meals per day ends the extra slot's rules by setting `endDateISO` instead
 * of deleting them (`adjustScheduleToDeclaredState`), so counting every reference that
 * ever had a rule would keep listing a meal the user dropped. Use this, not
 * `countMealPerDayFromScheduleRules`, wherever a dropped slot must not count.
 */
export function getLiveMealReferences(rules: ScheduleRule[], todayISO: string): number[] {
  const allReferences = new Set(rules
    .filter(rule => rule.itemType === Schedule_ItemType.Meal)
    .map(rule => rule.itemReference));

  const liveReferences: number[] = [];
  // for each meal slot, keep it if any weekday still has a rule in force for it
  for (const reference of allReferences) {
    for (let weekday = 1; weekday <= 7; ++weekday) {
      if (resolveRecurringRule(rules, Schedule_ItemType.Meal, todayISO, weekday, reference)) {
        liveReferences.push(reference);
        break;
      }
    }
  }
  return liveReferences.sort((a, b) => a - b);
}

export function generateScheduleParams(scheduleRules: ScheduleRule[], todayISO: string): ScheduleParams {
  // Schedule --------------------
  let scheduleParams: ScheduleParams = { unavailable: 'Schedule unavailable until you generate the plan.' } as any; // clear it all without lint complaints
  if (scheduleRules && scheduleRules.length > 0) {
    scheduleParams.unavailable = undefined;
    const weekdayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const wakeTime = getSameTimeEveryWeekday(scheduleRules, Schedule_ItemType.DayStart, todayISO);
    const sleepTime = getSameTimeEveryWeekday(scheduleRules, Schedule_ItemType.DayEnd, todayISO);

    // only the meal slots still in force, so a slot the user dropped is not listed
    const mealTimes = getLiveMealReferences(scheduleRules, todayISO)
      .map(reference => getSameTimeEveryWeekday(scheduleRules, Schedule_ItemType.Meal, todayISO, reference));

    let workoutDayNames = [];
    let workoutTime: number = undefined;
    let workoutWeeklyRules = scheduleRules.filter(rule => rule.itemType === Schedule_ItemType.Workout && rule.repeat === ScheduleRuleRepeat.Weekly && isRuleActiveOnDate(rule, todayISO));
    workoutWeeklyRules = workoutWeeklyRules.sort((a, b) => a.weekday - b.weekday);
    for (let iWorkout = 0; iWorkout < workoutWeeklyRules.length; ++iWorkout) {
      const workoutRule = workoutWeeklyRules[iWorkout];
      if (workoutRule) {
        workoutDayNames.push(weekdayNames[workoutRule.weekday - 1]);
        workoutTime = workoutTime !== undefined && workoutRule.time_hr !== workoutTime ? 0 : workoutRule.time_hr;
      }
    }

    let convertTime = (time_hr: number) => time_hr === 0 ? 'different times' : hoursToIsoTime(time_hr);

    scheduleParams.wakeup = convertTime(wakeTime);
    scheduleParams.sleep = convertTime(sleepTime);
    scheduleParams.workouts = workoutDayNames.join(', ') + ' at ' + convertTime(workoutTime);
    scheduleParams.meals = mealTimes.map(convertTime).join(', ');
  }

  return scheduleParams;
}